import { useMemo, useState } from 'react';
import { SectionCard, fmtNum, Input } from '../yeni-design/primitives';
import type { ReportConfig } from '../../types/panelConfig';
import { menuKaynagi } from './ekranKaynaklari';
import { useSaHamSorgu } from './useSatisVerisi';
import { BlokIskeleti } from './BlokIskeleti';
import { oranMetni } from './bicim';

const RAPOR_ADI = 'Birlikte Satılan Ürünler (son 90 gün)';

interface Cift {
  urun_a: string;
  urun_b: string;
  birlikte_fis: number;
  birliktelik_yuzde: number;
  /** A'nin GECTIGI fis sayisi — yuzdenin paydasi. */
  urun_a_fis?: number;
}

interface Props {
  reports: ReportConfig[];
  sube?: string | null;
  /**
   * G2: sepet ciftleri 90 gunluk onceden hesapli; kanala SUZULMEZ. G2b/S3 fix1: kapsam notu ("kanal
   * süzgeci uygulanmaz") artik BLOKTA yazilmiyor — TiklaAc basligindaki Bilgi'ye tasindi (pages/SatisAnaliz.tsx,
   * yalniz kanal seciliyken); burada tekrar basilirsa CIFT gorunurdu.
   */
  kanal?: string | null;
}

export interface Urun {
  ad: string;
  fis: number;
  esSayisi: number;
}

/**
 * Secilebilir urunler: `urun_a` kolonunda GECEN urunler. Canlida olculdu
 * (Gizova, 2026-08-28): 15.370 cift ama yalnizca 200 farkli `urun_a` —
 * yani liste kucuk, arama ISTEMCIDE yapilabilir.
 */
export function urunleriTopla(ciftler: Cift[]): Urun[] {
  const map = new Map<string, Urun>();
  for (const c of ciftler) {
    const ad = String(c.urun_a);
    const v = map.get(ad) ?? { ad, fis: 0, esSayisi: 0 };
    // `urun_a_fis` her satirda AYNI (A'nin kendi fis sayisi); max ile aliyoruz
    // ki kolon hic gelmezse 0 kalsin ve ekranda "—" basilsin.
    v.fis = Math.max(v.fis, Number(c.urun_a_fis ?? 0));
    v.esSayisi += 1;
    map.set(ad, v);
  }
  return Array.from(map.values()).sort((a, b) => b.fis - a.fis || a.ad.localeCompare(b.ad, 'tr'));
}

/**
 * Arama: Turkce'ye duyarli, aksan/kucuk-buyuk fark etmez. `localeCompare`
 * degil `toLocaleLowerCase('tr')` kullaniliyor cunku "İSTANBUL".toLowerCase()
 * ASCII kurallariyla "i̇stanbul" uretir ve "istanbul" aramasi TUTMAZ.
 */
export function urunAra(urunler: Urun[], sorgu: string): Urun[] {
  const q = sorgu.trim().toLocaleLowerCase('tr');
  if (!q) return urunler;
  return urunler.filter((u) => u.ad.toLocaleLowerCase('tr').includes(q));
}

export function SepetBlok({ reports, sube = null }: Props) {
  const kaynak = menuKaynagi(reports, 19, RAPOR_ADI);
  const [sorgu, setSorgu] = useState('');
  const [secili, setSecili] = useState<string | null>(null);

  // 🔴 Parametre YOK — ds onceden hesaplanmis `cdc_basket_pairs` tablosundan okur (tarih/@LOKASYON almaz).
  // Tek sorgu semasi yine `['sa', id, {}]`.
  const q = useSaHamSorgu<Cift>(kaynak, {});

  const ciftler = useMemo(() => q.data?.data ?? [], [q.data]);
  const urunler = useMemo(() => urunleriTopla(ciftler), [ciftler]);
  const bulunan = useMemo(() => urunAra(urunler, sorgu), [urunler, sorgu]);

  // Secim yoksa EN COK FISTE GECEN urun gosterilir: blok bos acilirsa kullanici
  // once bir sey aramak zorunda kalir ve "veri yok" sanabilir.
  const aktif = secili ?? urunler[0]?.ad ?? null;
  const aktifUrun = urunler.find((u) => u.ad === aktif) ?? null;

  const esler = useMemo(
    () =>
      ciftler
        .filter((c) => String(c.urun_a) === aktif)
        .map((c) => ({
          ad: String(c.urun_b),
          birlikte: Number(c.birlikte_fis ?? 0),
          yuzde: Number(c.birliktelik_yuzde ?? 0),
        }))
        .sort((a, b) => b.birlikte - a.birlikte),
    [ciftler, aktif],
  );

  if (!kaynak) return null; // rapor yok/ad tutmuyor -> sorgu bile acilmaz
  if (q.isLoading)
    return (
      <BlokIskeleti
        testid="blok-sepet-iskelet"
        altBaslik="Bir ürün seçin — onunla birlikte satılanları görün"
        yukseklik={320}
        satir={6}
      />
    );
  if (ciftler.length === 0) return null; // bos durum kurali

  return (
    <div data-testid="blok-sepet">
      <SectionCard
        // Baslik BASILMAZ: TiklaAc kabugu ayni metni bir kez zaten gosteriyor (bkz. TiklaAc.tsx).
        subtitle={
          <>
            Bir ürün seçin — onunla aynı fişte satılanlar{' '}
            {/* 🔴 Kisit EKRANDA yaziyor: bu rapor onceden hesaplanmis bir
                tablodan okuyor, dolayisiyla ustteki donem ve sube secicilerine
                YANIT VERMEZ. Sessizce firma geneli/90 gunluk rakam basmak,
                baslikta "ATAKENT · Ağustos" yazarken govdede baska bir seyi
                gostermek olurdu. */}
            <span className="text-[11px] text-ink-400 font-normal">
              {' '}· son 90 gün, dönem seçiminden bağımsız
              {sube !== null && ' · firma geneli (bu rapor şubeye ayrışmıyor)'}
            </span>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-3">
            {/* Sol: arama + urun listesi */}
            <div className="flex flex-col gap-2 min-w-0">
              <Input
                value={sorgu}
                onChange={(e) => setSorgu(e.target.value)}
                placeholder="Ürün ara…"
                data-testid="sepet-arama"
              />
              <div
                className="border border-border rounded overflow-y-auto"
                style={{ maxHeight: 220 }}
                data-testid="sepet-urun-listesi"
              >
                {bulunan.length === 0 ? (
                  <div className="text-[12px] text-ink-400 p-3" data-testid="sepet-bulunamadi">
                    “{sorgu}” için ürün yok
                  </div>
                ) : (
                  bulunan.map((u) => (
                    <button
                      key={u.ad}
                      type="button"
                      data-testid={`sepet-urun-${u.ad}`}
                      aria-pressed={u.ad === aktif}
                      onClick={() => setSecili(u.ad)}
                      className={`w-full text-left px-2.5 py-1.5 text-[12px] border-b border-border last:border-b-0 ${
                        u.ad === aktif ? 'bg-accent-50 text-ink-900 font-medium' : 'text-ink-600'
                      }`}
                    >
                      <span className="truncate block">{u.ad}</span>
                      <span className="text-[11px] text-ink-400 tnum">{fmtNum(u.fis)} fiş</span>
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Sag: secili urunun sepet dokumu */}
            <div className="min-w-0" data-testid="sepet-detay">
              {aktifUrun === null ? null : (
                <>
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-2">
                    <span className="text-[14px] font-semibold text-ink-900">{aktifUrun.ad}</span>
                    <span className="text-[12px] text-ink-500 tnum" data-testid="sepet-ozet">
                      {fmtNum(aktifUrun.fis)} fişte geçiyor · {fmtNum(aktifUrun.esSayisi)} farklı ürünle birlikte
                    </span>
                  </div>
                  {esler.length === 0 ? (
                    <div className="text-[12px] text-ink-400">Bu ürünle birlikte satılan kayıt yok.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-[12.5px]">
                        <thead>
                          <tr className="text-ink-400 text-left">
                            <th className="font-normal py-1">Birlikte satılan</th>
                            <th className="font-normal py-1 text-right">Fiş</th>
                            <th className="font-normal py-1 text-right">Birliktelik</th>
                          </tr>
                        </thead>
                        <tbody>
                          {esler.slice(0, 15).map((e) => (
                            <tr key={e.ad} data-testid={`sepet-es-${e.ad}`} className="border-t border-border">
                              <td className="py-1 pr-2 text-ink-800 truncate">{e.ad}</td>
                              <td className="py-1 text-right tnum text-ink-700">{fmtNum(e.birlikte)}</td>
                              <td className="py-1 text-right tnum text-ink-700">{oranMetni(e.yuzde, 1)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {/* Kirpma SESSIZ olmamali: 197 ese kadar cikabiliyor. */}
                      {esler.length > 15 && (
                        <div className="text-[11px] text-ink-400 pt-1.5" data-testid="sepet-kirpma-notu">
                          İlk 15 gösteriliyor — toplam {fmtNum(esler.length)} eş
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </SectionCard>
    </div>
  );
}
